import React, { useRef, useEffect } from 'react';
import styles from './Dropdown.module.scss';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  label?: string;
  value: string;
  options: DropdownOption[];
  placeholder?: string;
  error?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  label,
  value,
  options,
  placeholder = 'Select an option',
  error,
  onChange,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && <label className="text-sm font-medium text-white">{label}</label>}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-4 py-2 bg-[#030E1F] rounded-lg text-left flex items-center justify-between hover:bg-[#0a1b34] transition-colors ${
            error ? 'border border-red-500' : 'border border-[#1f2937]'
          } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <span className={value ? 'text-white' : 'text-[#A2A7B4]'}>
            {selectedOption?.label || placeholder}
          </span>
          <svg width="7" height="4" viewBox="0 0 7 4" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[7px] h-[4px]">
            <path d="M2.807 3.373L0.129999 0.696C0.0933325 0.659333 0.062333 0.616334 0.0369997 0.567C0.012333 0.518334 0 0.466 0 0.41C0 0.298 0.0366663 0.201667 0.11 0.121C0.184666 0.0403336 0.282666 0 0.403999 0H6.081C6.20167 0 6.299 0.0413329 6.373 0.124C6.447 0.206666 6.484 0.302666 6.484 0.412C6.484 0.44 6.44067 0.534667 6.354 0.696L3.677 3.373C3.615 3.435 3.54833 3.48267 3.477 3.516C3.40567 3.54933 3.32733 3.566 3.242 3.566C3.15667 3.566 3.07833 3.54933 3.007 3.516C2.93567 3.48267 2.869 3.435 2.807 3.373Z" fill="white"/>
          </svg>
        </button>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        {isOpen && (
          <div 
            className={`absolute top-full mt-2 w-full z-20 bg-[#030E1F] rounded-[12px] shadow-lg overflow-hidden max-h-60 overflow-y-auto ${styles.dropdownOptions}`}
          >
            {options.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full h-[50px] flex items-center justify-center text-base hover:bg-[#1f2937] transition-colors ${
                  value === option.value ? 'bg-[#3b82f6] text-white' : 'text-white'
                } ${index !== options.length - 1 ? 'border-b border-[#111E33]' : ''}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
